import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Database, Plus } from "lucide-react";
import { api, errMsg, fmtBytes, fmtNum, fmtUptime } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, LoadingRows, Metric, MetricGrid, PageHeader } from "@/components/shared";
import ServerMonitor from "@/components/ServerMonitor";

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="overview-page">
      <PageHeader overline="Visão geral" title="Estado do motor">
        <Button asChild className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">
          <Link to="/databases" data-testid="overview-new-db-button"><Plus className="h-4 w-4 mr-1" /> Novo banco</Link>
        </Button>
      </PageHeader>

      {!data ? (
        <LoadingRows />
      ) : (
        <>
          <MetricGrid>
            <Metric label="Bancos" value={fmtNum(data.databases)} accent testId="metric-databases" />
            <Metric label="Chaves (aprox.)" value={fmtNum(data.keys_approx)} hint="memtable + SSTables" testId="metric-keys" />
            <Metric label="Vetores" value={fmtNum(data.vectors)} hint="indexados + pendentes" testId="metric-vectors" />
            <Metric label="Disco" value={fmtBytes(data.disk_bytes)} hint={data.data_dir} testId="metric-disk" />
            <Metric label="RAM do motor (RSS)" value={fmtBytes(data.rss_bytes)} hint="processo axond" testId="metric-rss" />
            <Metric label="Uptime" value={fmtUptime(data.uptime_secs)} testId="metric-uptime" />
            <Metric label="Versão" value={`v${data.version}`} hint="axondb · Rust" testId="metric-version" />
            <Metric label="Atualização" value="5 s" hint="métricas ao vivo" testId="metric-refresh" />
          </MetricGrid>

          <ServerMonitor />

          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold">Bancos</h2>
              <Link to="/databases" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="overview-all-dbs-link">
                Ver todos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {data.recent.length === 0 ? (
              <EmptyState icon={Database} title="Nenhum banco ainda" description="Crie o primeiro banco para receber uma API própria com chave-valor e vetores." testId="overview-empty"
                action={<Button asChild variant="outline" className="rounded-sm border-zinc-700"><Link to="/databases" data-testid="overview-empty-create">Criar banco</Link></Button>} />
            ) : (
              <div className="panel divide-y divide-zinc-800">
                {data.recent.map((d, i) => (
                  <Link key={d.name} to={`/databases/${d.name}`} data-testid={`overview-db-${d.name}`} style={{ animationDelay: `${i * 40}ms` }}
                    className="row-enter flex items-center justify-between px-5 py-4 hover:bg-zinc-800/60 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <Database className="h-4 w-4 text-orange-500 shrink-0" />
                      <span className="font-mono text-sm truncate">{d.name}</span>
                    </div>
                    <div className="hidden sm:flex gap-6 text-xs text-zinc-400 font-mono">
                      <span>{fmtNum(d.keys_approx)} chaves</span>
                      <span>{fmtNum(d.vectors)} vetores</span>
                      <span>{fmtBytes(d.disk_bytes)}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-zinc-600" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
